import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { tv } from 'tailwind-variants'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { Sort, Tick, ChevronUp, ChevronDown } from 'components/icons'

// Menu width (w-56 = 14rem) used to right-align the portalled dropdown to the trigger.
const MENU_WIDTH = 224

const sortMenuStyles = tv({
  slots: {
    wrapper: 'relative',
    trigger:
      'flex items-center justify-center w-12 h-10 rounded-lg gap-1 border border-divider bg-surface hover:bg-surface-sunken',
    triggerIcon: 'flex items-center text-muted',
    // Portalled to <body> with fixed positioning so it escapes any scroll/overflow container it opens inside.
    menu: 'fixed w-56 bg-surface border border-divider rounded-lg overflow-hidden z-[1000] py-1',
    sectionLabel: 'px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted',
    sectionList: 'flex flex-col px-1 gap-0.5',
    divider: 'my-1 border-t border-divider',
    option: 'w-full h-8 px-3 text-sm gap-2 justify-start text-body',
    optionIcon: 'flex items-center w-3 h-3 shrink-0',
    optionLabel: 'flex-1 text-left',
    optionTick: 'text-blue shrink-0'
  },
  variants: {
    open: {
      true: { trigger: 'bg-surface-sunken' }
    },
    active: {
      true: { option: 'bg-really-light-blue text-blue hover:bg-really-light-blue' }
    }
  }
})

export type SortDirection = 'asc' | 'desc'

export interface SortValue<TBy extends string = string> {
  by: TBy
  direction: SortDirection
}

export interface SortOption<TBy extends string = string> extends SortValue<TBy> {
  label: string
}

export interface SortSection<TBy extends string = string> {
  label: string
  options: SortOption<TBy>[]
}

interface SortMenuProps<TBy extends string = string> {
  value: SortValue<TBy>
  onChange: (next: SortValue<TBy>) => void
  sections: SortSection<TBy>[]
  ariaLabel?: string
  tooltip?: string
}

const SortMenu = <TBy extends string = string>({
  value,
  onChange,
  sections,
  ariaLabel = 'Sort',
  tooltip = 'Sort'
}: SortMenuProps<TBy>): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const {
    wrapper,
    trigger,
    triggerIcon,
    menu,
    sectionLabel,
    sectionList,
    divider,
    option,
    optionIcon,
    optionLabel,
    optionTick
  } = sortMenuStyles()

  // Position the portalled menu just below the trigger, right-aligned to it (clamped on-screen).
  // Measure the wrapper (a plain DOM node) rather than the Button, whose ref is consumed by Tooltip.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return
    const rect = wrapperRef.current.getBoundingClientRect()
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
    setMenuPosition({ top: rect.bottom + 4, left })
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node
      const insideTrigger = wrapperRef.current?.contains(target)
      const insideMenu = menuRef.current?.contains(target)
      if (!insideTrigger && !insideMenu) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    // The trigger scrolls away with its container (the menu is fixed), so close on any scroll.
    const handleScroll = () => setOpen(false)
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open])

  const renderOption = (sortOption: SortOption<TBy>) => {
    const active = sortOption.by === value.by && sortOption.direction === value.direction
    return (
      <Button
        key={`${sortOption.by}-${sortOption.direction}`}
        internalType="transparent"
        onClick={() => {
          onChange({ by: sortOption.by, direction: sortOption.direction })
          setOpen(false)
        }}
        className={option({ active })}
      >
        <span className={optionIcon()}>
          {sortOption.direction === 'asc' ? (
            <ChevronUp width={12} height={12} />
          ) : (
            <ChevronDown width={12} height={12} />
          )}
        </span>
        <span className={optionLabel()}>{sortOption.label}</span>
        {active && (
          <span className={optionTick()}>
            <Tick width={12} height={12} />
          </span>
        )}
      </Button>
    )
  }

  return (
    <div ref={wrapperRef} className={wrapper()}>
      <Tooltip content={tooltip}>
        <Button
          internalType="transparent"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((previous) => !previous)}
          className={trigger({ open })}
        >
          <span className={triggerIcon()}>
            <Sort width={16} height={16} />
          </span>
          <span className={triggerIcon()}>
            {value.direction === 'asc' ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
          </span>
        </Button>
      </Tooltip>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            // The id lets a parent popover (components/menu) recognise clicks here as "inside" and stay open.
            id="sort-menu-options"
            role="menu"
            className={menu()}
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {sections.map((section, index) => (
              <React.Fragment key={section.label}>
                {index > 0 && <div className={divider()} />}
                <div className={sectionLabel()}>{section.label}</div>
                <div className={sectionList()}>{section.options.map(renderOption)}</div>
              </React.Fragment>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}

export default SortMenu
